import React, { useState } from "react";

interface FlowStage {
    id: number;
    label: string;
    color?: string;
    order: number;
    days?: number;
    startDate?: string;
    endDate?: string;
}

interface FlowTransition {
    fromStageId: number;
    toStageId: number;
    label: string;
    days?: number;
    startDate?: string;
    endDate?: string;
}

interface StageFlowProps {
    stages: FlowStage[];
    transitions: FlowTransition[];
    isLoading?: boolean;
}

const globalStyles = `
@keyframes cardEntry {
    0% { opacity: 0; transform: translateY(5px); }
    100% { opacity: 1; transform: translateY(0); }
}
@keyframes tooltipAppear {
    0% { opacity: 0; transform: translate(-50%, -115%) scale(0.92); }
    60% { opacity: 1; transform: translate(-50%, -110%) scale(1.015); }
    100% { opacity: 1; transform: translate(-50%, -110%) scale(1); }
}
`;

if (typeof document !== "undefined") {
    const style = document.createElement("style");
    style.innerHTML = globalStyles;
    document.head.appendChild(style);
}
// Wrap text inside card
const wrapText = (text: string, maxChars = 16) => {
    const words = text.split(" ");
    const lines: string[] = [];
    let line = "";

    words.forEach(w => {
        if ((line + w).length <= maxChars) {
            line += w + " ";
        } else {
            lines.push(line.trim());
            line = w + " ";
        }
    });

    if (line.trim().length > 0) lines.push(line.trim());
    return lines;
};

const FIXED_STAGES = [
    { id: 1, label: "Feature Created", order: 1, apiLabels: ["Feedback Created", "Feature Created"] },
    { id: 2, label: "Evidence Gathering", order: 2, apiLabels: ["Evidence Gathering"] },
    { id: 3, label: "Pending Triage", order: 3, apiLabels: ["Pending Triage"] },
    { id: 4, label: "Needs More Information", order: 4, apiLabels: ["Needs More Information"] },
    { id: 5, label: "Parked for Further Consideration", order: 5, apiLabels: ["Parked for Further Consideration", "Parked"] },
    { id: 6, label: "Rejected", order: 6, apiLabels: ["Rejected"] },
    { id: 7, label: "In Engineering", order: 7, apiLabels: ["In Engineering"] },
    { id: 8, label: "Generally Available", order: 8, apiLabels: ["Generally Available", "GA"] }
];

// Helper function to map API stage to fixed stage
const mapToFixedStage = (apiLabel: string) => {
    return FIXED_STAGES.find(fixed =>
        fixed.apiLabels.some(label =>
            label.toLowerCase() === apiLabel.toLowerCase() ||
            apiLabel.toLowerCase().includes(label.toLowerCase()) ||
            label.toLowerCase().includes(apiLabel.toLowerCase())
        )
    );
};

const StageFlow: React.FC<StageFlowProps> = ({ stages, transitions, isLoading }) => {
    // Hook must be declared before any early returns (Rules of Hooks)
    const [hoverTransition, setHoverTransition] = useState<null | {
        step: number;
        fromLabel: string;
        toLabel: string;
        days: string | number;
        startDate: string;
        endDate: string;
        pctX: number;
        pctY: number;
        isBackward: boolean;
        isRepeat: boolean;
    }>(null);

    if (isLoading) return <p>Loading flow...</p>;
    if (!stages || stages.length === 0) return <p>No flow data found.</p>;

    // Map API stages to fixed stage positions
    const mappedStages = stages.map(apiStage => {
        const fixedStage = mapToFixedStage(apiStage.label);
        return {
            ...apiStage,
            fixedOrder: fixedStage?.order ?? null,
            fixedLabel: fixedStage?.label ?? apiStage.label
        };
    }).filter(s => s.fixedOrder !== null);

    /* ------------------------------------------------------------
          Derive start date + latest stage + visited stages
    ------------------------------------------------------------- */
    const earliestTransition = transitions
        .filter(t => t.startDate)
        .sort((a, b) => new Date(a.startDate!).getTime() - new Date(b.startDate!).getTime())[0];

    if (earliestTransition && mappedStages.length > 0) {
        mappedStages[0].startDate = earliestTransition.startDate!;
    }

    const latestTransition = transitions
        .filter(t => t.endDate)
        .sort((a, b) => new Date(b.endDate!).getTime() - new Date(a.endDate!).getTime())[0];

    const latestStageId = latestTransition?.toStageId ?? null;

    // Find current stage
    const currentStage = mappedStages.find(s => s.id === latestStageId);
    const isCurrentRejected = currentStage?.fixedLabel === "Rejected";

    // Build set of all visited stage IDs (completed stages)
    const visitedStageIds = new Set<number>();
    transitions.forEach(t => {
        visitedStageIds.add(t.fromStageId);
        visitedStageIds.add(t.toStageId);
    });

    // Detect repeats and reversals (exclude last transition from repeat count)
    const stageTransitionCount = new Map<number, number>();
    const hasReversal = new Map<number, boolean>();

    transitions.forEach((t, idx) => {
        const fromStage = mappedStages.find(s => s.id === t.fromStageId);
        const toStage = mappedStages.find(s => s.id === t.toStageId);

        if (fromStage && toStage && fromStage.fixedOrder && toStage.fixedOrder) {
            // Count transitions to same stage (repeats), but exclude the last transition
            if (idx < transitions.length - 1) {
                stageTransitionCount.set(t.toStageId, (stageTransitionCount.get(t.toStageId) || 0) + 1);
            }

            // Check if moving backward (reversal)
            if (toStage.fixedOrder < fromStage.fixedOrder) {
                hasReversal.set(t.toStageId, true);
            }
        }
    });



    /* ------------------------------------------------------------
            RESPONSIVE SVG LAYOUT FIX
    ------------------------------------------------------------- */
    const totalStages = FIXED_STAGES.length;
    const lineXOffset = 9.9; // Optional horizontal offset for vertical timeline lines (tuned from 280.5 -> 290.4)
    const cardPadding = 8;
    const lineHeight = 15;

    const allWrapped = FIXED_STAGES.map(stage => wrapText(stage.label, 14));
    const maxLines = Math.max(1, ...allWrapped.map(w => w.length));
    const cardHeight = maxLines * lineHeight + cardPadding * 2 + 24; // Space for badge

    const startY = 80;
    const transitionBaseY = startY + cardHeight + 30;
    const transitionGap = 45;

    // Calculate width with proper gaps between cards
    const cardWidth = 120; // Reduced foreignObject width
    const gapBetweenCards = 50; // Increased gap for more spacing between stage cards
    const svgInternalWidth = (cardWidth * totalStages) + (gapBetweenCards * (totalStages + 1));

    const getX = (order: number) => {
        // Position cards evenly with gaps
        return gapBetweenCards + (order - 1) * (cardWidth + gapBetweenCards) + cardWidth / 2;
    };

    const svgHeight = transitionBaseY + transitions.length * transitionGap + 60;

    // (Moved useState hook above early returns to satisfy lint rule)

    return (
        <div style={{
            width: "98%",
            maxWidth: "98%",
            margin: "0 auto",
            padding: "0",
            position: "relative",
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
        }}>
            <svg
                width="100%"
                height={svgHeight}
                viewBox={`0 0 ${svgInternalWidth} ${svgHeight}`}
                preserveAspectRatio="xMidYMid meet"
                style={{
                    background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                    border: "1px solid rgba(226, 232, 240, 0.8)",
                    borderRadius: 16,
                    display: "block",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)",
                }}
            >
                <defs>
                    {/* Line gradient */}
                    <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#94a3b8" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#cbd5e1" stopOpacity="0.5" />
                    </linearGradient>

                    {/* Forward arrow */}
                    <marker id="arrow-forward" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
                        <polygon points="0 0, 7 3.5, 0 7" fill="#10b981" />
                    </marker>

                    {/* Backward arrow */}
                    <marker id="arrow-backward" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
                        <polygon points="0 0, 7 3.5, 0 7" fill="#ef4444" />
                    </marker>

                    <filter id="card-shadow" x="-50%" y="-50%" width="200%" height="200%">
                        <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#000" floodOpacity="0.2" />
                    </filter>

                    <style>
                        {`
                        .circle-label { 
                            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                            font-weight: 600;
                        }
                        .transition-label { 
                            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                            font-weight: 700;
                            letter-spacing: -0.01em;
                        }
                    `}
                    </style>
                </defs>

                {/* ------------------------------------------------------------
                        Render Hardcoded (Fixed) Stage Cards
                ------------------------------------------------------------- */}
                {FIXED_STAGES.map((stage) => {
                    const x = getX(stage.order);
                    const wrapped = wrapText(stage.label, 14);

                    // Check if this stage is actually in the flow
                    const stageInFlow = mappedStages.find(s => s.fixedOrder === stage.order);
                    const isVisited = stageInFlow && visitedStageIds.has(stageInFlow.id);
                    const isCurrent = stageInFlow && stageInFlow.id === latestStageId;
                    const isRejected = isCurrent && isCurrentRejected;
                    const hasRepeat = stageInFlow && (stageTransitionCount.get(stageInFlow.id) || 0) > 1;
                    const hasBackward = stageInFlow && hasReversal.get(stageInFlow.id);

                    const isFirst = stageInFlow && earliestTransition ?
                        mappedStages[0]?.id === stageInFlow.id : false;

                    // Determine color based on state:
                    // - Grey: not visited
                    // - Green: visited but not current
                    // - Blue: current (not rejected)
                    // - Red: current and rejected
                    let cardBackground = "rgba(248, 250, 252, 0.9)"; // Grey default slightly stronger
                    let cardBorder = "1.5px solid rgba(226, 232, 240, 0.9)";
                    let cardShadow = "0px 1px 2px rgba(0,0,0,0.06)";
                    let textColor = "#6b7f97";
                    let dotColor = "#c8d1de";
                    let lineColor = "rgba(203, 213, 225, 0.9)";
                    let opacity = 0.88;

                    if (isVisited) {
                        if (isRejected) {
                            // Red for rejected current state — further softened
                            cardBackground = "linear-gradient(135deg, #ffe4e6 0%, #fee2e2 100%)";
                            cardBorder = "1.5px solid #f87171"; // slightly stronger
                            cardShadow = "0 3px 10px rgba(239, 68, 68, 0.16), 0 2px 5px rgba(239, 68, 68, 0.12)";
                            textColor = "#9f1239";
                            dotColor = "#f87171";
                            lineColor = "#f87171";
                            opacity = 0.6;
                        } else if (isCurrent) {
                            // Blue for current (non-rejected)
                            cardBackground = "linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)";
                            cardBorder = "2px solid #3b82f6";
                            cardShadow = "0 6px 20px rgba(59, 130, 246, 0.25), 0 2px 8px rgba(59, 130, 246, 0.15)";
                            textColor = "#1e40af";
                            dotColor = "#3b82f6";
                            lineColor = "#3b82f6";
                            opacity = 1;
                        } else {
                            // Green for completed (visited but not current) — further softened
                            cardBackground = "linear-gradient(135deg, #ecfdf5 0%, #e7f8ee 100%)";
                            cardBorder = "0.9px solid #86efac"; // softer border
                            cardShadow = "0 3px 10px rgba(16, 185, 129, 0.12), 0 2px 5px rgba(16, 185, 129, 0.08)";
                            textColor = "#115e59";
                            dotColor = "#86efac";
                            lineColor = "#86efac"; // soften vertical line
                            opacity = 0.9;
                        }
                    }

                    return (
                        <g key={stage.id}>
                            {/* Solid Vertical Timeline Line - passes through card center (subpixel crisp) */}
                            <line
                                x1={x + lineXOffset + 0.5}
                                y1={startY + cardHeight / 2 + 18}
                                x2={x + lineXOffset + 0.5}
                                y2={svgHeight - 40}
                                stroke={lineColor}
                                strokeWidth={isVisited ? 3 : 2}
                                opacity="0.6"
                            />

                            {/* Timeline dot at card position */}
                            <circle
                                cx={x + lineXOffset + 0.5}
                                cy={startY + cardHeight / 2 + 20}
                                r={isCurrent ? 8 : (isVisited ? 6 : 4)}
                                fill={dotColor}
                                opacity={opacity}
                                stroke="#fff"
                                strokeWidth={2}
                            />

                            {/* Card */}
                            <foreignObject
                                x={x - cardWidth / 2}
                                y={startY - cardHeight / 2}
                                width={cardWidth}
                                height={cardHeight}
                                style={{ overflow: "visible" }}
                            >
                                <div
                                    style={{
                                        width: `${cardWidth}px`,
                                        height: `${cardHeight}px`,
                                        background: cardBackground,
                                        border: cardBorder,
                                        borderRadius: "10px",
                                        boxShadow: cardShadow,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        padding: "8px 10px",
                                        fontSize: "12px",
                                        fontWeight: isVisited ? (isCurrent ? 700 : 600) : 500,
                                        color: textColor,
                                        opacity: opacity,
                                        animation: isVisited ? "cardEntry 260ms ease-out" : "none",
                                        transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                                        letterSpacing: "-0.01em",
                                        position: "relative",
                                    }}
                                    onMouseEnter={(e) => {
                                        if (isVisited) {
                                            e.currentTarget.style.transform = "translateY(-3px)";
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (isVisited) {
                                            e.currentTarget.style.transform = "translateY(0px)";
                                        }
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: "4px",
                                            width: "100%",
                                            textAlign: "center",
                                        }}
                                    >
                                        <div style={{ textAlign: "center", lineHeight: "1.3" }}>
                                            {wrapped.map((line, i) => (
                                                <div key={i}>{line}</div>
                                            ))}

                                            {/* Badge shown BELOW all text, centered */}
                                            {isCurrent && (
                                                <div
                                                    style={{
                                                        marginTop: "5px",
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        alignItems: "center",
                                                        gap: "4px"
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            background: isRejected
                                                                ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                                                                : "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                                                            color: "#fff",
                                                            fontSize: "9px",
                                                            fontWeight: 800,
                                                            padding: "3px 8px",
                                                            borderRadius: "6px",
                                                            whiteSpace: "nowrap",
                                                            boxShadow: isRejected
                                                                ? "0 2px 8px rgba(239, 68, 68, 0.4)"
                                                                : "0 2px 8px rgba(59, 130, 246, 0.4)",
                                                            letterSpacing: "0.05em",
                                                            textTransform: "uppercase",
                                                        }}
                                                    >
                                                        CURRENT
                                                    </span>
                                                    {/* Show days in current stage */}
                                                    {latestTransition && (
                                                        <div style={{
                                                            fontSize: "10px",
                                                            fontWeight: 600,
                                                            color: textColor,
                                                            opacity: 0.8
                                                        }}>
                                                            {latestTransition.days ?? "N/A"} Days
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Show repeat/reversal indicators */}
                                            {(hasRepeat || hasBackward) && (
                                                <div style={{
                                                    position: "absolute",
                                                    top: "-8px",
                                                    right: "-8px",
                                                    display: "flex",
                                                    gap: "2px",
                                                }}>
                                                    {hasRepeat && (
                                                        <span style={{
                                                            width: "16px",
                                                            height: "16px",
                                                            borderRadius: "50%",
                                                            background: "#f59e0b",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontSize: "10px",
                                                            fontWeight: 800,
                                                            color: "#fff",
                                                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                                        }} title="Stage repeated">
                                                            ↻
                                                        </span>
                                                    )}
                                                    {hasBackward && (
                                                        <span style={{
                                                            width: "16px",
                                                            height: "16px",
                                                            borderRadius: "50%",
                                                            background: "#ef4444",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontSize: "10px",
                                                            fontWeight: 800,
                                                            color: "#fff",
                                                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                                        }} title="Moved backward">
                                                            ←
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                    </div>
                                </div>
                            </foreignObject>
                            {/* Optional: Start date below first card */}
                            {
                                isFirst && earliestTransition?.startDate && (
                                    <text
                                        x="120"
                                        y={startY + cardHeight / 2 + 28}
                                        textAnchor="middle"
                                        fontSize={11}
                                        fontWeight="600"
                                        fill="#64748b"
                                        letterSpacing="0.01em"
                                    >
                                        {new Date(earliestTransition.startDate).toLocaleDateString()}
                                    </text>
                                )
                            }
                        </g>
                    );
                })}

                {/* ------------------------------------------------------------
                        Render Transitions (exclude circular loops)
                ------------------------------------------------------------- */}
                {transitions.map((t, idx) => {
                    const from = mappedStages.find(s => s.id === t.fromStageId);
                    const to = mappedStages.find(s => s.id === t.toStageId);
                    if (!from || !to || !from.fixedOrder || !to.fixedOrder) return null;


                    // Check if this is the last transition
                    const isLastTransition = idx === transitions.length - 1;

                    // Align horizontal transition endpoints with vertical line centers (include offset + subpixel)
                    const fromX = getX(from.fixedOrder) + lineXOffset + 0.5;
                    const toX = getX(to.fixedOrder) + lineXOffset + 0.5;
                    const y = transitionBaseY + idx * transitionGap;

                    const isForward = to.fixedOrder > from.fixedOrder;
                    const color = isForward ? "#10b981" : "#ef4444";
                    const marker = isForward ? "url(#arrow-forward)" : "url(#arrow-backward)";

                    const days = t.days ?? "N/A";
                    const startDate = t.startDate ? new Date(t.startDate).toLocaleDateString() : "N/A";
                    const endDate = t.endDate ? new Date(t.endDate).toLocaleDateString() : "N/A";

                    // For last transition, don't draw the line, only show circle and text
                    const pathD = isLastTransition ? "" : `M ${fromX} ${y} L ${toX} ${y}`;

                    const midX = (fromX + toX) / 2; // midpoint horizontal (percent values removed to satisfy lint)
                    const isBackward = !isForward;
                    const repeatCountTo = stageTransitionCount.get(to.id) || 0;
                    const repeatCountFrom = stageTransitionCount.get(from.id) || 0;
                    const isRepeat = repeatCountTo > 1 || repeatCountFrom > 1;

                    // Approximate text width to align small info icon on same row
                    const labelString = isLastTransition
                        ? "In this state"
                        : `Stage ${idx + 1} - ${days} Days`;
                    const secondLine = isLastTransition ? `for ${days} days` : endDate;
                    const approxCharWidth = 6; // rough average for fontSize 11 bold Inter
                    const textWidthApprox = labelString.length * approxCharWidth;
                    const labelY = y - 14; // baseline of first line
                    const textRightX = midX + textWidthApprox / 2;
                    const iconX = textRightX + 6; // immediate small gap after text
                    const iconY = labelY - 2; // slight upward to visually center with baseline
                    const iconPctX = (iconX / svgInternalWidth) * 100;
                    const iconPctY = (iconY / svgHeight) * 100;

                    return (
                        <g key={`${t.fromStageId}-${t.toStageId}-${idx}`}>
                            {!isLastTransition && <circle cx={fromX} cy={y} r={4} fill={color} opacity={0.9} />}

                            {!isLastTransition && (
                                <path
                                    d={pathD}
                                    stroke={color}
                                    strokeWidth={2}
                                    fill="none"
                                    strokeDasharray={isForward ? "6 4" : "4 3"}
                                    markerEnd={marker}
                                    opacity={0.85}
                                />
                            )}

                            {!isLastTransition && <circle cx={toX} cy={y} r={4} fill={color} opacity={0.9} />}

                            {/* Inline info icon will appear near label instead of midpoint */}

                            <g>
                                {/* First line (Step / Days) text only */}
                                <text
                                    x={isLastTransition ? toX : midX}
                                    y={labelY}
                                    textAnchor="middle"
                                    fontSize={11}
                                    className="transition-label"
                                    fill="#334155"
                                >
                                    <tspan x={isLastTransition ? toX : midX} dy="0" fontWeight="700">
                                        {labelString}
                                    </tspan>
                                </text>
                                {/* Second line date (no hover) */}
                                <text
                                    x={isLastTransition ? toX : midX}
                                    y={labelY + 13}
                                    textAnchor="middle"
                                    fontSize={isLastTransition ? 11 : 10}
                                    fontWeight={isLastTransition ? 700 : 600}
                                    fill={isLastTransition ? "#334155" : "#64748b"}
                                >
                                    {secondLine}
                                </text>
                                {/* Date on third line for last transition */}
                                {isLastTransition && (
                                    <text
                                        x={toX}
                                        y={labelY + 26}
                                        textAnchor="middle"
                                        fontSize={10}
                                        fontWeight={600}
                                        fill="#64748b"
                                    >
                                        {endDate}
                                    </text>
                                )}
                                {/* Info icon - only show for non-last transitions */}
                                {!isLastTransition && (
                                    <g
                                        onMouseEnter={() => setHoverTransition({
                                            step: idx + 1,
                                            fromLabel: from.fixedLabel,
                                            toLabel: to.fixedLabel,
                                            days,
                                            startDate,
                                            endDate,
                                            pctX: iconPctX,
                                            pctY: iconPctY,
                                            isBackward,
                                            isRepeat
                                        })}
                                        onMouseLeave={() => setHoverTransition(null)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <circle
                                            cx={iconX}
                                            cy={iconY}
                                            r={6}
                                            fill="#ffffff"
                                            stroke="rgba(148,163,184,0.55)"
                                            strokeWidth={1}
                                        />
                                        <text
                                            x={iconX}
                                            y={iconY + 2.5}
                                            textAnchor="middle"
                                            fontSize={7}
                                            fontWeight={700}
                                            fill="#3b82f6"
                                            style={{ pointerEvents: "none" }}
                                        >i</text>
                                    </g>
                                )}
                            </g>
                        </g>
                    );
                })}
            </svg>
            {hoverTransition && (
                <div
                    style={{
                        position: "absolute",
                        left: `${hoverTransition.pctX}%`,
                        top: `${hoverTransition.pctY}%`,
                        transform: "translate(-50%, -114%)",
                        background: "linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)",
                        padding: "12px 16px 14px 16px",
                        borderRadius: 16,
                        boxShadow: "0 14px 34px rgba(0,0,0,0.18), 0 4px 12px rgba(0,0,0,0.10)",
                        border: "1px solid rgba(203,213,225,0.65)",
                        minWidth: 210,
                        zIndex: 30,
                        fontSize: 12,
                        color: "#0f172a",
                        lineHeight: 1.45,
                        backdropFilter: "blur(10px)",
                        WebkitBackdropFilter: "blur(10px)",
                        pointerEvents: "none",
                        animation: "tooltipAppear 180ms cubic-bezier(0.33, 1, 0.68, 1)",
                        willChange: "transform, opacity",
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                        <span style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 22,
                            height: 22,
                            borderRadius: "6px",
                            background: "linear-gradient(135deg,#3b82f6,#2563eb)",
                            color: "#fff",
                            fontSize: 12,
                            fontWeight: 700,
                            boxShadow: "0 2px 6px rgba(59,130,246,0.35)"
                        }}>{hoverTransition.step}</span>
                        <div style={{ fontWeight: 700, fontSize: 13, letterSpacing: "-0.01em" }}>{`Stage ${hoverTransition.step} - ${hoverTransition.days} Days`}</div>
                    </div>
                    <div style={{ marginBottom: 4 }}><span style={{ color: "#334155", fontWeight: 600 }}>From:</span> {hoverTransition.fromLabel}</div>
                    <div style={{ marginBottom: 6 }}><span style={{ color: "#334155", fontWeight: 600 }}>To:</span> {hoverTransition.toLabel}</div>
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 8,
                        fontSize: 11,
                        color: "#475569",
                        marginBottom: 4
                    }}>
                        <div><span style={{ fontWeight: 600, color: "#64748b" }}>Start:</span><br />{hoverTransition.startDate}</div>
                        <div><span style={{ fontWeight: 600, color: "#64748b" }}>End:</span><br />{hoverTransition.endDate}</div>
                    </div>
                    {(hoverTransition.isBackward || hoverTransition.isRepeat) && (
                        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                            {hoverTransition.isBackward && (
                                <span style={{
                                    background: "#fee2e2",
                                    color: "#dc2626",
                                    fontSize: 10,
                                    fontWeight: 700,
                                    padding: "4px 8px",
                                    borderRadius: 8,
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.08)"
                                }}>Reversal</span>
                            )}
                            {hoverTransition.isRepeat && (
                                <span style={{
                                    background: "#fef3c7",
                                    color: "#b45309",
                                    fontSize: 10,
                                    fontWeight: 700,
                                    padding: "4px 8px",
                                    borderRadius: 8,
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.08)"
                                }}>Repeat</span>
                            )}
                        </div>
                    )}
                    {/* Bottom arrow pointer centered above icon */}
                    <div
                        style={{
                            position: "absolute",
                            left: "50%",
                            bottom: -10,
                            width: 22,
                            height: 14,
                            transform: "translateX(-50%)",
                            clipPath: "polygon(50% 100%, 0 0, 100% 0)",
                            background: "linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)",
                            borderLeft: "1px solid rgba(203,213,225,0.65)",
                            borderRight: "1px solid rgba(203,213,225,0.65)",
                            borderBottom: "1px solid rgba(203,213,225,0.65)",
                            boxShadow: "0 4px 10px rgba(0,0,0,0.08)"
                        }}
                    />
                </div>
            )}
        </div >
    );
};

export default StageFlow;
